<script setup lang="ts">
// TODO - AF-539 This should be refactor to use FormTemplate
import { computed, onMounted, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import type { CashDrawer, PaymentMethod } from '../../../types';
import { useAToast, useADialog } from '@Atoms';
import { ADOPT_URL } from '@Constants';
import { formatPhoneNumber, toCents } from '@Helpers';
import { useSessionStore } from '@Stores';
import { getIntakeFlags } from '@AnimalModule';
import { checkDuplicatePersonService, emailValidation, phoneValidation } from '@FormModule';
import { INTAKES_WITH_PAYMENT } from '../../../constants';
import { isCheckInIntakeType } from '../../../helpers';
import { useCartSteps } from '../../../composables';
import { CART_STEPS, PAYMENT_METHODS } from '../../../types';
import { SendCheckoutLinkService } from '../../../services';
import useCartStore from '../../../store';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { shelterProfile } = storeToRefs(useSessionStore());
const {
  cartData,
  cartId,
  cashDrawerOptions,
  isAdoption,
  isDonation,
  isIntake,
  isTransferOut,
  loading,
  outcomeType,
  pendingOutcomeRoute,
  people,
} = storeToRefs(useCartStore());

const { push } = useRouter();
const { notify, TOAST_TYPES } = useAToast();
const { currentStep, previousStep } = useCartSteps();
const { outcomeCompletedCartReset } = useCartStore();
const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();

let duplicateCheckRequestId = 0;
const CONFIRM_WORD = 'Proceed';
const deviceCheckoutMethods = [
  {
    icon: 'faWindowMaximize',
    label: 'cart.mobileCheckout.openTab',
    value: 'newTab',
  },
  {
    icon: 'faQrcode',
    label: 'cart.mobileCheckout.getQrCode',
    value: 'qrCode',
  },
  {
    icon: 'faLink',
    label: 'cart.mobileCheckout.sendLink',
    value: 'link',
  },
];

const cashDrawer = ref<CashDrawer | null>(null);
const adopterEmail = ref('');
const adopterEmailError = ref('');
const adopterPhone = ref('');
const adopterPhoneError = ref('');
const checkoutMethod = ref();
const deviceCheckoutMethod = ref('');
const paymentMethod = ref<PAYMENT_METHODS | null>(null);
const showErrorMessage = ref(false);
const receiptNotes = ref('');
const shelterDeviceSearchPhone = ref('');
const shelterDeviceSearchEmail = ref('');
const shelterDeviceSearchPhoneError = ref('');
const shelterDeviceSearchEmailError = ref('');
const duplicateResults = ref<any[]>([]);
const isCheckingDuplicate = ref(false);
const doNotAdoptConfirmed = ref(false);
const shelterDeviceSearchNoRecordFound = ref(false);
const adopterNoDoNotAdoptRecordFound = ref(false);

const isCheckInType = computed(() => isCheckInIntakeType(outcomeType.value));
// prettier-ignore
const checkoutOptionLabel = computed(() => isCheckInType.value ? 'cart.mobileCheckout.checkoutOptionCheckin'
  : isIntake.value ? 'cart.mobileCheckout.checkoutOptionIntake'
  : 'cart.mobileCheckout.checkoutOption');

const checkoutMethods = computed(() => [
  {
    icon: 'faMobile',
    // prettier-ignore
    label: isCheckInType.value ? 'cart.mobileCheckout.sendLinkCheckin'
      : isIntake.value ? 'cart.mobileCheckout.sendLinkIntake'
      : 'cart.mobileCheckout.sendLink',
    value: 'adopter',
  },
  {
    icon: 'faHome',
    // prettier-ignore
    label: isCheckInType.value ? 'cart.mobileCheckout.shelterDeviceCheckin'
      : isIntake.value? 'cart.mobileCheckout.shelterDeviceIntake'
      : 'cart.mobileCheckout.shelterDevice',
    value: 'shelter',
  },
]);
const adopterAppLink = computed(() => ADOPT_URL + '/mobile-checkout/' + cartId.value);
const isIntakeWithPayment = computed(() => INTAKES_WITH_PAYMENT.includes(cartData.value?.outcome_type));
const intakeHasPaymentEnabled = computed(() => getIntakeFlags(outcomeType.value, shelterProfile.value).payment);
const showCashDrawer = computed((): boolean =>
  // prettier-ignore
  isTransferOut.value ? false
    : !isIntake.value ? !!shelterProfile.value?.cash_drawer
    : !!shelterProfile.value?.cash_drawer && isIntakeWithPayment.value && intakeHasPaymentEnabled.value
);
const showCheckoutLinkError = computed(
  () =>
    (checkoutMethod.value === 'adopter' && !adopterEmail.value && !adopterPhone.value) ||
    !!adopterPhoneError.value ||
    !!adopterEmailError.value
);

const hasDoNotAdoptPerson = computed(
  () => isAdoption.value && duplicateResults.value.some((person: any) => checkDoNotAdoptFlag(person))
);
const isSubmitBlockedByDoNotAdopt = computed(() => hasDoNotAdoptPerson.value && !doNotAdoptConfirmed.value);
const showFormErrors = computed(() => showErrorMessage.value && !isSubmitBlockedByDoNotAdopt.value);
const isSubmitBlockedByDuplicateCheck = computed(
  () =>
    isCheckingDuplicate.value &&
    ((checkoutMethod.value === 'adopter' && (adopterPhone.value?.trim() || adopterEmail.value?.trim())) ||
      (checkoutMethod.value === 'shelter' &&
        (shelterDeviceSearchPhone.value?.trim() || shelterDeviceSearchEmail.value?.trim())))
);
const showPaymentMethod = computed((): boolean =>
  // prettier-ignore
  isTransferOut.value ? false
    : !isIntake.value ? true
    : isIntakeWithPayment.value && intakeHasPaymentEnabled.value
);

const showShelterCheckoutMethod = computed(
  () => (checkoutMethod.value === 'shelter' && !deviceCheckoutMethod.value) || shelterCheckoutByLinkHasError.value
);

const shelterCheckoutByLinkHasError = computed(() => deviceCheckoutMethod.value === 'link' && !adopterEmail.value);

const isBelowMinimumAmount = computed(() => {
  const total = parseFloat(cartData.value?.total) || 0;
  const totalCents = toCents(total);
  const minimumCents = 50;

  return totalCents > 0 && totalCents < minimumCents;
});

const paymentOptionCreditCard = computed(() => {
  const disabledForEmptyTotal = !parseFloat(cartData.value?.total);
  const disableCreditCard = !shelterProfile.value?.stripe;
  const disabledForBelowMinimum = isBelowMinimumAmount.value;

  // prettier-ignore
  const message = disableCreditCard ? 'shelterInfo.settings.stripe.notIntegrated'
    : disabledForBelowMinimum ? 'cart.title.paymentMethodRequiresMinimumCreditCard'
    : 'cart.title.noCreditCardPaymentAvailable';

  return {
    icon: 'faCreditCard',
    key: PAYMENT_METHODS.CREDIT,
    label: 'cart.paymentOptions.creditCard',
    value: PAYMENT_METHODS.CREDIT,
    disabled: disabledForEmptyTotal || disableCreditCard || disabledForBelowMinimum,
    disabledMessage: message,
  };
});

const paymentOptions = computed((): PaymentMethod[] => [
  paymentOptionCreditCard.value,
  {
    icon: 'faCashRegister',
    key: PAYMENT_METHODS.OTHER,
    label: 'cart.paymentOptions.cashCheckOther',
    value: PAYMENT_METHODS.OTHER,
    disabled: false,
  },
]);

watch(adopterEmail, () => onEmailUpdate());
watch(adopterPhone, () => onPhoneUpdate());
watch([adopterEmailError, adopterPhone], () => onRequiredFieldsUpdate());
watch(isBelowMinimumAmount, () => onAmountValidation());
watch(checkoutMethod, () => onCheckoutMethodUpdate());
watch(shelterDeviceSearchPhone, () => onShelterDeviceSearchPhoneUpdate());
watch(shelterDeviceSearchEmail, () => onShelterDeviceSearchEmailUpdate());
watch(hasDoNotAdoptPerson, (val) => {
  if (val && !doNotAdoptConfirmed.value) openDoNotAdoptModal();
});
watch(deviceCheckoutMethod, (val) => {
  if (val) showErrorMessage.value = false;
});

onMounted(() => (currentStep.value = CART_STEPS.MOBILE_CHECKOUT));

function checkDoNotAdoptFlag(person: any) {
  return !!person.do_not_adopt;
}

function resetDoNotAdoptState() {
  duplicateResults.value = [];
  doNotAdoptConfirmed.value = false;
  shelterDeviceSearchNoRecordFound.value = false;
}

function performDuplicateCheck(field: 'phone' | 'email', value: string, options?: { source: 'shelter' }) {
  if (!isAdoption.value) return;

  duplicateCheckRequestId++;
  const currentId = duplicateCheckRequestId;

  duplicateResults.value = [];
  doNotAdoptConfirmed.value = false;
  shelterDeviceSearchNoRecordFound.value = false;
  isCheckingDuplicate.value = true;

  const PAYLOAD = {
    entity: 'people',
    field,
    value,
    return_do_not_adopt: true,
  };

  checkDuplicatePersonService(PAYLOAD)
    .then((result) => {
      if (currentId !== duplicateCheckRequestId) return;

      const results = Array.isArray(result?.data) ? result?.data : [];
      duplicateResults.value = results;

      const noDoNotAdoptFound = !results.some((p: any) => checkDoNotAdoptFlag(p));
      options?.source === 'shelter'
        ? (shelterDeviceSearchNoRecordFound.value = noDoNotAdoptFound)
        : (adopterNoDoNotAdoptRecordFound.value = noDoNotAdoptFound);
    })
    .finally(() => {
      if (currentId === duplicateCheckRequestId) isCheckingDuplicate.value = false;
    });
}

function resetDuplicateCheck() {
  duplicateResults.value = [];
  doNotAdoptConfirmed.value = false;
  shelterDeviceSearchNoRecordFound.value = false;
  adopterNoDoNotAdoptRecordFound.value = false;
}

function onAdopterPhoneBlur() {
  const phone = adopterPhone.value?.trim();
  if (!phone) return resetDuplicateCheck();
  if (adopterPhoneError.value) return;

  performDuplicateCheck('phone', formatPhoneNumber(phone) || phone);
}

function onAdopterEmailBlur() {
  const email = adopterEmail.value?.trim();
  if (!email) return resetDuplicateCheck();
  if (adopterEmailError.value) return;

  performDuplicateCheck('email', email.toLowerCase());
}

function onShelterDeviceSearchPhoneBlur() {
  const phone = shelterDeviceSearchPhone.value?.trim();
  if (!phone) return resetDuplicateCheck();
  if (shelterDeviceSearchPhoneError.value) return;

  performDuplicateCheck('phone', formatPhoneNumber(phone) || phone, { source: 'shelter' });
}

function onShelterDeviceSearchEmailBlur() {
  const email = shelterDeviceSearchEmail.value?.trim();
  if (!email) return resetDuplicateCheck();
  if (shelterDeviceSearchEmailError.value) return;

  performDuplicateCheck('email', email.toLowerCase(), { source: 'shelter' });
}

function onShelterDeviceSearchPhoneUpdate() {
  shelterDeviceSearchNoRecordFound.value = false;
  resetDoNotAdoptState();
  showErrorMessage.value = false;

  // prettier-ignore
  !shelterDeviceSearchPhone.value ? (shelterDeviceSearchPhoneError.value = '')
    : !phoneValidation(shelterDeviceSearchPhone.value) ? (shelterDeviceSearchPhoneError.value = 'components.forms.errors.validPhone')
    : (shelterDeviceSearchPhoneError.value = '');
}

function onShelterDeviceSearchEmailUpdate() {
  shelterDeviceSearchNoRecordFound.value = false;
  resetDoNotAdoptState();
  showErrorMessage.value = false;

  // prettier-ignore
  !shelterDeviceSearchEmail.value ? (shelterDeviceSearchEmailError.value = '')
    : !emailValidation(shelterDeviceSearchEmail.value) ? (shelterDeviceSearchEmailError.value = 'components.forms.errors.validEmail')
    : (shelterDeviceSearchEmailError.value = '');
}

function openDoNotAdoptModal() {
  activeModalProps.value = {
    onConfirm: () => {
      doNotAdoptConfirmed.value = true;
      showErrorMessage.value = false;
    },
    results: duplicateResults.value,
  };
  activeModal.value = SYSTEM_MODALS.CART_DO_NOT_ADOPT_CONFIRM;
  dialogVisibility.value = true;
}

// EVENTS
function onRequiredFieldsUpdate() {
  if (adopterEmailError.value && adopterEmailError.value) {
    showErrorMessage.value = showCheckoutLinkError.value && showShelterCheckoutMethod.value;
  }
}

function onEmailUpdate() {
  resetDoNotAdoptState();
  showErrorMessage.value = false;
  if (!adopterEmail.value) return (adopterEmailError.value = '');

  const notValidEmail = !emailValidation(adopterEmail.value);
  if (notValidEmail) return (adopterEmailError.value = 'components.forms.errors.validEmail');

  adopterEmailError.value = '';
}

function onPhoneUpdate() {
  resetDoNotAdoptState();
  showErrorMessage.value = false;
  if (!adopterPhone.value) return (adopterPhoneError.value = '');

  const notValidPhone = !phoneValidation(adopterPhone.value);
  if (notValidPhone) return (adopterPhoneError.value = 'components.forms.errors.validPhone');

  adopterPhoneError.value = '';
}

function onAmountValidation() {
  if (isBelowMinimumAmount.value && paymentMethod.value === PAYMENT_METHODS.CREDIT) {
    paymentMethod.value = null;
  }
}

function onNextStep() {
  if (isSubmitBlockedByDuplicateCheck.value) return;

  if (isSubmitBlockedByDoNotAdopt.value) {
    showErrorMessage.value = true;
    openDoNotAdoptModal();
    return;
  }

  const paymentMethodError = !showPaymentMethod.value ? false : !paymentMethod.value;
  const hasError =
    paymentMethodError || !checkoutMethod.value || showCheckoutLinkError.value || showShelterCheckoutMethod.value;
  if (hasError) return (showErrorMessage.value = true);

  if (showCashDrawer.value && !cashDrawer.value) {
    return (showErrorMessage.value = true);
  }

  showErrorMessage.value = false;
  loading.value = true;

  // NOTE: This is a expected setting in the adopter app
  // prettier-ignore
  const paymentType = !showPaymentMethod.value ? null
    : paymentMethod.value === PAYMENT_METHODS.CREDIT ? paymentMethod.value 
    : PAYMENT_METHODS.CASH;

  const email =
    checkoutMethod.value === 'shelter' && deviceCheckoutMethod.value !== 'link'
      ? shelterDeviceSearchEmail.value
      : adopterEmail.value;
  const phone =
    checkoutMethod.value === 'shelter' && deviceCheckoutMethod.value !== 'link'
      ? shelterDeviceSearchPhone.value
      : adopterPhone.value;

  const PAYLOAD: any = {
    _id: cartId.value,
    cart_id: cartId.value,
    checkoutFlow: checkoutMethod.value,
    email,
    payment_type: paymentType,
    phone,
    show_application: null, // NOTE: There is no feature using this for now
    show_contract: null, // NOTE: There is no feature using this for now
    status: 'pending',
  };

  if (showCashDrawer.value) {
    PAYLOAD.cash_drawer = cashDrawer.value?.label;
    PAYLOAD.cash_drawer_id = cashDrawer.value?._id.$oid;
  }

  if (receiptNotes.value) {
    PAYLOAD.receipt_notes = receiptNotes.value;
  }

  if (doNotAdoptConfirmed.value) {
    PAYLOAD.staff_confirmed_use_of_do_not_adopt_person = true;
  }

  const TOAST_ERROR = {
    message: 'errors.generics.unknownIssue.message',
    title: 'errors.generics.unknownIssue.title',
    type: TOAST_TYPES.ERROR,
  };

  SendCheckoutLinkService(PAYLOAD)
    .then(() => {
      // NOTE :: TS compliance - pendingOutcomeRoute should always exist when sending checkout link
      if (!pendingOutcomeRoute.value) {
        Sentry.captureException(new Error('pendingOutcomeRoute is null during onSendCheckoutLink'), {
          tags: { component: 'MCartStepMobileCheckout', type: 'ts-compliance' },
        });
      }

      if (deviceCheckoutMethod.value === 'newTab') window.open(adopterAppLink.value, '_blank');
      push(pendingOutcomeRoute.value!).then(() => outcomeCompletedCartReset());
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

function onCheckoutMethodUpdate() {
  adopterEmail.value = '';
  adopterPhone.value = '';
  shelterDeviceSearchPhone.value = '';
  shelterDeviceSearchEmail.value = '';
  adopterEmailError.value = '';
  adopterPhoneError.value = '';
  shelterDeviceSearchPhoneError.value = '';
  shelterDeviceSearchEmailError.value = '';
  showErrorMessage.value = false;
  adopterNoDoNotAdoptRecordFound.value = false;
  resetDoNotAdoptState();

  if (checkoutMethod.value === 'adopter' && people.value) {
    const { email, phone } = people.value;

    if (email) adopterEmail.value = email;
    if (phone) adopterPhone.value = phone;
  }
}
</script>

<template>
  <div class="flex h-fit flex-col gap-4">
    <main class="flex flex-1 flex-col gap-4">
      <MCashDrawer
        v-if="showCashDrawer"
        v-model="cashDrawer"
        :loading
        :cashDrawerError="showFormErrors && showCashDrawer && !cashDrawer"
        :cashDrawerOptions
        @onChange="showErrorMessage = false"
      />

      <!-- Payment Method -->
      <div
        v-if="showPaymentMethod"
        class="flex flex-col gap-2"
      >
        <AFieldLabel
          required
          :error="showFormErrors && !paymentMethod"
          :label="$t('cart.mobileCheckout.paymentMethod')"
        />

        <div class="flex flex-wrap gap-1 sm:gap-4">
          <AFieldCheckboxButton
            v-model="paymentMethod"
            :options="paymentOptions"
          />
        </div>
      </div>

      <!-- Checkout Method -->
      <div class="flex flex-col gap-2">
        <AFieldLabel
          required
          :error="showErrorMessage && !checkoutMethod"
          :label="$t(checkoutOptionLabel)"
        />

        <div class="flex flex-wrap gap-1 sm:gap-4">
          <AFieldCheckboxButton
            v-model="checkoutMethod"
            :options="checkoutMethods"
          />
        </div>
      </div>

      <!-- Send Link Options -->
      <div
        v-if="checkoutMethod === 'adopter'"
        class="flex flex-col gap-2"
      >
        <AFieldLabel
          required
          :error="showFormErrors && showCheckoutLinkError"
          :label="$t('cart.mobileCheckout.deliveryMethod')"
        />

        <div class="flex w-96 max-w-full flex-col justify-center gap-1">
          <div class="flex flex-col items-start">
            <AFieldPhone
              v-model="adopterPhone"
              :error="(showErrorMessage && showCheckoutLinkError) || (adopterPhoneError && !adopterEmail)"
              :placeholder="$t('cart.mobileCheckout.enterPhone')"
              @blur="onAdopterPhoneBlur"
            />

            <AFieldError
              v-if="adopterPhoneError && !adopterEmail"
              :error="adopterPhoneError"
            />
          </div>

          <div class="flex flex-col items-center">
            <span class="text-sm font-semibold">
              {{ $t('common.or') }}
            </span>
          </div>

          <form class="flex flex-col items-start">
            <AFieldEmail
              v-model="adopterEmail"
              :error="(showFormErrors && showCheckoutLinkError) || (adopterEmailError && !adopterPhone)"
              :inProgress="isCheckingDuplicate"
              :placeholder="$t('cart.mobileCheckout.enterEmail')"
              @blur="onAdopterEmailBlur"
            />

            <AFieldError
              v-if="adopterEmailError && !adopterPhone"
              :error="adopterEmailError"
            />
          </form>

          <div
            v-if="isAdoption && adopterNoDoNotAdoptRecordFound"
            class="flex items-center gap-2 text-sm font-medium text-green-900"
          >
            <i class="pi pi-check" />

            <p>{{ $t('cart.mobileCheckout.noDoNotAdoptRecordFound') }}</p>
          </div>
        </div>
      </div>

      <!-- Shelter Device Options -->
      <div
        v-if="checkoutMethod === 'shelter'"
        class="flex flex-col gap-2"
      >
        <div
          v-if="isAdoption"
          class="mb-3 flex max-w-[500px] flex-col gap-2"
        >
          <AFieldLabel :label="$t('cart.mobileCheckout.verifyDoNotAdoptFlag')" />

          <div class="flex flex-col gap-1">
            <AFieldPhone
              v-model="shelterDeviceSearchPhone"
              :error="shelterDeviceSearchPhoneError"
              :placeholder="$t('cart.mobileCheckout.checkClientPhone')"
              @blur="onShelterDeviceSearchPhoneBlur"
            />

            <AFieldError :error="shelterDeviceSearchPhoneError" />

            <AFieldEmail
              v-model="shelterDeviceSearchEmail"
              :error="shelterDeviceSearchEmailError"
              :inProgress="isCheckingDuplicate"
              :placeholder="$t('cart.mobileCheckout.checkClientEmail')"
              @blur="onShelterDeviceSearchEmailBlur"
            />

            <AFieldError :error="shelterDeviceSearchEmailError" />

            <div
              v-if="shelterDeviceSearchNoRecordFound"
              class="flex items-center gap-2 text-sm font-medium text-green-900"
            >
              <i class="pi pi-check" />

              <p>{{ $t('cart.mobileCheckout.noDoNotAdoptRecordFound') }}</p>
            </div>
          </div>
        </div>

        <AFieldLabel
          required
          :error="showFormErrors && showShelterCheckoutMethod"
          :label="$t('cart.mobileCheckout.deviceCheckoutMethod')"
        />

        <div class="flex flex-wrap gap-4">
          <AFieldCheckboxButton
            v-model="deviceCheckoutMethod"
            class="text-nowrap"
            :options="deviceCheckoutMethods"
          />
        </div>

        <div
          v-if="deviceCheckoutMethod === 'link'"
          class="max-w-[500px]"
        >
          <AFieldEmail
            v-model="adopterEmail"
            :error="(showFormErrors && showShelterCheckoutMethod) || adopterEmailError"
            :placeholder="$t('cart.mobileCheckout.enterEmail')"
          />

          <AFieldError :error="adopterEmailError" />
        </div>
      </div>

      <!-- Receipt Notes -->
      <div
        v-if="!isDonation && !isTransferOut"
        class="flex max-w-[500px] flex-col gap-2"
      >
        <AFieldLabel :label="$t('cart.label.receiptNotes')" />

        <AFieldTextArea v-model="receiptNotes" />
      </div>
    </main>

    <footer class="flex-shrink">
      <Message
        v-if="showErrorMessage && !isSubmitBlockedByDoNotAdopt"
        pt:text="text-sm"
        severity="error"
        :closable="false"
      >
        {{ $t('cart.mobileCheckout.formError') }}
      </Message>

      <Message
        v-else-if="showErrorMessage && isSubmitBlockedByDoNotAdopt"
        pt:text="text-sm"
        severity="error"
        :closable="false"
      >
        {{ $t('cart.doNotAdopt.typeToConfirm', { word: CONFIRM_WORD }) }}
      </Message>

      <div class="flex flex-wrap gap-1 sm:justify-between">
        <Button
          outlined
          class="af-button af-button-success-outlined w-fit"
          icon="pi pi-arrow-left"
          :loading
          :label="$t('common.back')"
          @click="emits('onStepUpdate', previousStep)"
        />

        <Button
          class="af-button af-button-success sm:ml-auto"
          iconPos="right"
          :loading
          :disabled="!!isSubmitBlockedByDuplicateCheck"
          :label="$t('cart.label.submitAndComplete')"
          @click="onNextStep"
        >
          <template #icon>
            <FaIcon
              class="mr-2"
              :icon="$icons.faPaw"
            />
          </template>
        </Button>
      </div>
    </footer>
  </div>
</template>
